import React, { useState } from 'react';
import '../styles/LoginScreen.css';
import LanguageSwitcher from './shared/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useBodyBackground } from '../hooks/useBodyBackground';

function LoginScreen({ onLoginSuccess }) {
  useBodyBackground('#ffffff');
  const { t } = useTranslation();
  const [accessCode, setAccessCode] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [showSmsInput, setShowSmsInput] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  const handleAccessCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await api.login(accessCode);

      if (data.smsVerificationRequired && !data.isOwner && !data.isAdmin) {
        setPendingUserId(data.userId);
        setShowSmsInput(true);
        setError('');
      } else {
        onLoginSuccess(data);
      }
    } catch (err) {
      setError(err.message || t('login.accessDenied'));
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSmsCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await api.verifySmsCode(pendingUserId, smsCode);
      onLoginSuccess(data);
    } catch (err) {
      setError(err.message || t('login.verificationFailed'));
      console.error('SMS verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowSmsInput(false);
    setSmsCode('');
    setPendingUserId(null);
    setError('');
  };

  const handleFocus = () => {
    setIsKeyboardOpen(true);
  };

  const handleBlur = () => {
    setIsKeyboardOpen(false);
  };

  return (
    <div className={`login-page ${isKeyboardOpen ? 'keyboard-open' : ''}`}>
      <div className="language-switcher-wrapper">
        <LanguageSwitcher />
      </div>

      <div className="logo-container">
        <div className="logo-text">Baroom</div>
      </div>

      <p className="app-subtitle">{t('login.subtitle')}</p>

      {!showSmsInput ? (
        <form onSubmit={handleAccessCodeSubmit} className="login-form">
          <div className="input-group">
            <label htmlFor="accessCode" className="input-label">
              {t('login.accessCode')}
            </label>
            <input
              type="password"
              id="accessCode"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={t('login.accessCodePlaceholder')}
              className="input-field"
              required
              disabled={loading}
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? (
              <span className="button-loading">
                <span className="spinner-small"></span>
                {t('login.signingIn')}
              </span>
            ) : (
              t('login.signIn')
            )}
          </button>
        </form>
      ) : (
        <form onSubmit={handleSmsCodeSubmit} className="login-form">
          <div className="sms-notice">
            <p>{t('login.smsNotice')}</p>
          </div>

          <div className="input-group">
            <label htmlFor="smsCode" className="input-label">
              {t('login.verificationCode')}
            </label>
            <input
              type="text"
              id="smsCode"
              value={smsCode}
              onChange={(e) => setSmsCode(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={t('login.verificationCodePlaceholder')}
              className="input-field"
              maxLength="6"
              required
              disabled={loading}
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={loading}
          >
            {loading ? (
              <span className="button-loading">
                <span className="spinner-small"></span>
                {t('login.verifying')}
              </span>
            ) : (
              t('login.verify')
            )}
          </button>

          <button
            type="button"
            onClick={handleBackToLogin}
            className="back-button"
            disabled={loading}
          >
            {t('login.backToLogin')}
          </button>
        </form>
      )}

      <div className="login-footer">
        <p className="powered-by">
          {t('login.poweredBy')}
        </p>
      </div>
    </div>
  );
}

export default LoginScreen;
